/**
 * "Confirm it's you" — shown when a big account action (billing, team members, sales-channel
 * connections) needs a fresh identity check. See utils/reauth.ts for how it is triggered.
 *
 * Accepts the password, or a code emailed to the account (people who sign in from Shopify never
 * set a TCGEasy password), plus the authenticator code when 2FA is on. On success the action
 * that asked is retried automatically.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { api } from '../../utils/api'
import { registerReauthHandler, type ReauthRequest } from '../../utils/reauth'
import { ActionConfirmModal } from '../modals/ActionConfirmModal'
import styles from './ReauthPrompt.module.css'

type Method = 'password' | 'email'

export function ReauthPrompt() {
  const [request, setRequest] = useState<ReauthRequest | null>(null)
  const [method, setMethod] = useState<Method>('password')
  const [password, setPassword] = useState('')
  const [emailCode, setEmailCode] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null)

  useEffect(() => {
    registerReauthHandler(
      (next) =>
        new Promise<boolean>((resolve) => {
          resolveRef.current = resolve
          setMethod('password')
          setPassword('')
          setEmailCode('')
          setMfaCode('')
          setEmailSentTo(null)
          setError(null)
          setRequest(next)
        })
    )
    return () => {
      registerReauthHandler(null)
      // Never leave a waiting request hanging: unmounting mid-question counts as "cancel".
      resolveRef.current?.(false)
      resolveRef.current = null
    }
  }, [])

  const finish = (confirmed: boolean) => {
    resolveRef.current?.(confirmed)
    resolveRef.current = null
    setRequest(null)
  }

  const sendEmailCode = async () => {
    setSending(true)
    setError(null)
    try {
      const res = await api.post('/auth/confirm-identity/email-code', {})
      setEmailSentTo(res.data?.data?.sentTo ?? 'your email')
      setMethod('email')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'We could not send the email. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const firstFactorReady = method === 'password' ? password.length > 0 : emailCode.trim().length > 0
  const ready = firstFactorReady && (!request?.mfaRequired || mfaCode.trim().length > 0)

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    if (!ready || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await api.post('/auth/confirm-identity', {
        ...(method === 'password' ? { password } : { emailCode: emailCode.trim() }),
        ...(request?.mfaRequired ? { code: mfaCode.trim() } : {}),
      })
      finish(true)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'That did not work. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ActionConfirmModal
      isOpen={request !== null}
      title="Confirm it's you"
      message="This changes your account (billing, team or a connected store), so we need to check it's really you. You won't be asked again for 10 minutes."
      confirmLabel="Confirm"
      isLoading={submitting}
      confirmDisabled={!ready}
      onConfirm={() => void submit()}
      onCancel={() => finish(false)}
    >
      <form onSubmit={submit} className={styles.field}>
        {method === 'password' ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="reauth-password">Password</label>
            <input
              id="reauth-password"
              className={styles.input}
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" className={styles.linkButton} onClick={() => void sendEmailCode()} disabled={sending}>
              {sending ? 'Sending…' : "Don't know your password? Email me a code instead"}
            </button>
            <p className={styles.hint}>
              Signed up through Shopify? Re-opening TCGEasy from your Shopify admin also confirms it's you.
            </p>
          </div>
        ) : (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="reauth-email-code">Code from the email</label>
            <p className={styles.hint}>We sent an 8-digit code to {emailSentTo}. It expires in 10 minutes.</p>
            <input
              id="reauth-email-code"
              className={styles.input}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value)}
            />
            <button type="button" className={styles.linkButton} onClick={() => void sendEmailCode()} disabled={sending}>
              {sending ? 'Sending…' : 'Send a new code'}
            </button>
            <button type="button" className={styles.linkButton} onClick={() => setMethod('password')}>
              Use my password instead
            </button>
          </div>
        )}

        {request?.mfaRequired ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="reauth-mfa">Authenticator code</label>
            <input
              id="reauth-mfa"
              className={styles.input}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
            />
            <p className={styles.hint}>Or one of your backup codes.</p>
          </div>
        ) : null}

        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        {/* Lets Enter submit; the modal's own button does the same. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </ActionConfirmModal>
  )
}
